import {
  AlertTriangle,
  CalendarDays,
  FileText,
  ListChecks,
  Stethoscope,
  UserRound,
} from 'lucide-react';

import {
  getAssessmentReportAudienceLabel,
  getCbdfChapterLabel,
  formatIcfQualifiedCode,
  getIcfQualifierContextLabel,
  getIcfQualifierLabel,
  ICF_DOMAIN_CATALOG,
  type AssessmentReportFindingStatus,
  type IcfDomain,
} from '../reports/assessmentReportCatalogs';
import {
  getAssessmentReportDomainLabel,
  getAssessmentReportFindingStatusLabel,
  getAssessmentReportPeriod,
  normalizeAssessmentReportPayload,
  validateAssessmentReportPayload,
  type AssessmentReportIcfProfileV2,
  type AssessmentReportSourceSnapshotV2,
} from '../reports/assessmentReportDomain';
import { getAssessmentReportPresentation, getAssessmentReportPurposeText } from '../reports/assessmentReportPresentation';
import AssessmentLongitudinalPanel from '../components/AssessmentLongitudinalPanel';
import AssessmentClinicalSourcesPanel from '../components/AssessmentClinicalSourcesPanel';

export interface AssessmentReportRendererProps {
  payloadJson: unknown;
  className?: string;
}

type IcfEntry =
  AssessmentReportIcfProfileV2['domains'][keyof AssessmentReportIcfProfileV2['domains']][number];

const joinClasses = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

const formatCalendarDate = (value: string | null | undefined): string | null => {
  if (!value) return null;

  const calendarDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (calendarDate) {
    const [, year, month, day] = calendarDate;
    return `${day}/${month}/${year}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR').format(parsed);
};

const formatDateTime = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};

const getFindingStatusTone = (status: AssessmentReportFindingStatus): string => {
  switch (status) {
    case 'within_expected':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800';
    case 'borderline':
      return 'border-amber-200 bg-amber-50 text-amber-900';
    case 'altered':
      return 'border-rose-200 bg-rose-50 text-rose-800';
    case 'not_interpretable':
      return 'border-violet-200 bg-violet-50 text-violet-800';
    default:
      return 'border-slate-200 bg-slate-100 text-slate-700';
  }
};

const getFindingStatusDotTone = (status: AssessmentReportFindingStatus): string => {
  switch (status) {
    case 'within_expected':
      return 'bg-emerald-500';
    case 'borderline':
      return 'bg-amber-500';
    case 'altered':
      return 'bg-rose-500';
    case 'not_interpretable':
      return 'bg-violet-500';
    default:
      return 'bg-slate-400';
  }
};

const FindingStatusBadge = ({ status }: { status: AssessmentReportFindingStatus }) => (
  <span
    className={joinClasses(
      'inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold',
      getFindingStatusTone(status),
    )}
    aria-label={`Situação do achado: ${getAssessmentReportFindingStatusLabel(status)}`}
  >
    <span
      className={joinClasses('h-1.5 w-1.5 rounded-full', getFindingStatusDotTone(status))}
      aria-hidden="true"
    />
    {getAssessmentReportFindingStatusLabel(status)}
  </span>
);

const MetadataItem = ({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string | null;
}) => (
  <div className="min-w-0 py-1 print:break-inside-avoid">
    <dt className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{label}</dt>
    <dd className="mt-1 text-sm font-bold leading-snug text-slate-900">{value}</dd>
    {detail ? <dd className="mt-1 text-xs leading-relaxed text-slate-600">{detail}</dd> : null}
  </div>
);

const getSourceInterpretation = (source: AssessmentReportSourceSnapshotV2): string | null =>
  source.finding.conciseSummary?.trim()
  || null;

const formatPrimaryMeasure = (source: AssessmentReportSourceSnapshotV2): string => {
  const measure = source.snapshot.primaryMeasure;
  const formattedValue = measure.formattedValue.trim();
  const unit = measure.unit?.trim();

  if (!unit || formattedValue.toLocaleLowerCase('pt-BR').includes(unit.toLocaleLowerCase('pt-BR'))) {
    return formattedValue;
  }
  return `${formattedValue} ${unit}`;
};

const NarrativeBlock = ({
  title,
  text,
}: {
  title: string;
  text: string | null | undefined;
}) => {
  const normalizedText = text?.trim();
  if (!normalizedText) return null;

  return (
    <section className="rounded-[22px] border border-slate-200 bg-white p-5 shadow-sm print:break-inside-avoid print:shadow-none">
      <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">{title}</h3>
      <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
        {normalizedText}
      </p>
    </section>
  );
};

const getIcfDomainLabel = (domain: IcfDomain): string =>
  ICF_DOMAIN_CATALOG.find((item) => item.value === domain)?.label ?? 'Domínio CIF';

const getIcfQualifierSummary = (entry: IcfEntry): string => {
  if (entry.domain === 'activities_and_participation') {
    return [
      `Desempenho: ${getIcfQualifierLabel(entry.qualifier, entry.domain)}`,
      entry.capacityQualifier !== null
        ? `Capacidade: ${getIcfQualifierLabel(entry.capacityQualifier, entry.domain)}`
        : null,
    ].filter(Boolean).join(' · ');
  }

  return `${getIcfQualifierContextLabel(entry.qualifierContext)} · ${String(entry.qualifier)} · ${getIcfQualifierLabel(entry.qualifier, entry.domain)}`;
};

const IcfDomainGroup = ({
  domain,
  entries,
}: {
  domain: IcfDomain;
  entries: readonly IcfEntry[];
}) => {
  if (entries.length === 0) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 print:break-inside-avoid">
      <h3 className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">
        {getIcfDomainLabel(domain)}
      </h3>
      <div className="mt-3 space-y-3">
        {entries.map((entry) => (
          <div key={entry.id} className="rounded-xl bg-white p-3 ring-1 ring-slate-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-slate-900 px-2 py-1 font-mono text-xs font-bold text-white">
                {formatIcfQualifiedCode(
                  entry.code,
                  entry.qualifier,
                  entry.capacityQualifier,
                )}
              </span>
              <span className="rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs font-bold text-primary-800">
                {getIcfQualifierSummary(entry)}
              </span>
            </div>
            <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-800">
              {entry.professionalDescription}
            </p>
            {entry.note ? (
              <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{entry.note}</p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AssessmentReportRenderer({
  payloadJson,
  className,
}: AssessmentReportRendererProps) {
  const originalPayload = normalizeAssessmentReportPayload(payloadJson);
  const validation = originalPayload ? validateAssessmentReportPayload(originalPayload) : null;
  const payload = originalPayload ? getAssessmentReportPresentation(originalPayload) : null;

  if (!payload || !validation?.valid) {
    return (
      <div
        className={joinClasses(
          'rounded-[24px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-900',
          className,
        )}
        role="alert"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <div>
            <div className="font-black">Relatório indisponível para visualização</div>
            <p className="mt-1 leading-relaxed">
              O conteúdo não corresponde à versão atual ou está clinicamente incompleto.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const period = getAssessmentReportPeriod(payload.sources);
  const hasClassification = payload.sources.some((source) =>
    source.finding.status === 'within_expected'
    || source.finding.status === 'borderline'
    || source.finding.status === 'altered');
  const hasInterpretation = payload.sources.some((source) => getSourceInterpretation(source));
  const recipientDetails = [
    payload.audience.recipientName,
    payload.audience.recipientRole,
    payload.audience.organization,
  ].filter((value): value is string => Boolean(value?.trim()));
  const recipientType =
    payload.audience.type === 'other' && payload.audience.customLabel
      ? payload.audience.customLabel
      : getAssessmentReportAudienceLabel(payload.audience.type);
  const professionalDetails = [
    payload.authorship.professional.role,
    payload.authorship.professional.registration,
  ].filter((value): value is string => Boolean(value?.trim()));
  const icfGroups: Array<{
    domain: IcfDomain;
    entries: readonly IcfEntry[];
  }> = [
    {
      domain: 'body_functions',
      entries: payload.icf.domains.bodyFunctions,
    },
    {
      domain: 'body_structures',
      entries: payload.icf.domains.bodyStructures,
    },
    {
      domain: 'activities_and_participation',
      entries: payload.icf.domains.activitiesAndParticipation,
    },
    {
      domain: 'environmental_factors',
      entries: payload.icf.domains.environmentalFactors,
    },
  ];
  const icfEntryCount = icfGroups.reduce((total, group) => total + group.entries.length, 0);
  const hasCbdfContent = payload.cbdf.entries.length > 0 || Boolean(payload.cbdf.professionalNote);
  const hasIcfContent = icfEntryCount > 0 || Boolean(payload.icf.professionalSynthesis);

  return (
    <article
      className={joinClasses(
        'space-y-5 text-slate-800 print:space-y-4 print:bg-white',
        className,
      )}
      aria-label={payload.title}
    >
      <header className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm print:break-inside-avoid print:rounded-none print:shadow-none">
        <div className="border-b border-slate-200 bg-slate-50/80 px-5 py-5 sm:px-6">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
            <div className="flex min-w-0 items-start gap-3">
              <span className="rounded-2xl bg-primary-50 p-3 text-primary-700">
                <FileText className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-[0.24em] text-primary-700">
                  Relatório de avaliações
                </div>
                <h1 className="mt-1 text-xl font-black leading-tight text-slate-950 sm:text-2xl">
                  {payload.title}
                </h1>
              </div>
            </div>
            <div className="shrink-0 text-left text-xs leading-relaxed text-slate-500 sm:text-right">
              <div>Atualizado em {formatDateTime(payload.updatedAt) ?? 'data não registrada'}</div>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-5 py-5 sm:px-6">
          <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
            <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                Paciente
              </div>
              <div className="mt-1 text-sm font-black text-slate-900">{payload.patient.name}</div>
              {(payload.patient.medicalRecord || payload.patient.birthDate) ? (
                <div className="mt-1 text-xs text-slate-600">
                  {[
                    payload.patient.medicalRecord
                      ? `Prontuário ${payload.patient.medicalRecord}`
                      : null,
                    payload.patient.birthDate
                      ? `Nascimento ${formatCalendarDate(payload.patient.birthDate) ?? payload.patient.birthDate}`
                      : null,
                  ].filter(Boolean).join(' · ')}
                </div>
              ) : null}
            </div>
          </div>

          <dl className="grid gap-x-5 gap-y-2 border-y border-slate-100 py-2 sm:grid-cols-3">
            <MetadataItem label="Finalidade" value={getAssessmentReportPurposeText(payload.purpose)} />
            {period ? <MetadataItem label="Período avaliado" value={period.label} /> : null}
            <MetadataItem
              label="Destinatário"
              value={recipientType}
              detail={recipientDetails.length > 0 ? recipientDetails.join(' · ') : null}
            />
          </dl>

          <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <Stethoscope className="mt-0.5 h-4 w-4 shrink-0 text-primary-700" aria-hidden="true" />
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                Profissional responsável pela síntese
              </div>
              <div className="mt-1 text-sm font-black text-slate-900">
                {payload.authorship.professional.name ?? 'Profissional não identificado'}
              </div>
              {professionalDetails.length > 0 ? (
                <div className="mt-1 text-xs text-slate-600">{professionalDetails.join(' · ')}</div>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      {payload.purpose.type !== 'other' ? (
        <NarrativeBlock title="Contexto do relatório" text={payload.purpose.description} />
      ) : null}
      <NarrativeBlock title="Demanda do encaminhamento" text={payload.purpose.referralQuestion} />
      <AssessmentClinicalSourcesPanel sources={payload.sources} />

      {payload.documentModel === 'longitudinal' ? (
        <section className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm print:shadow-none" aria-label="Evolução longitudinal dos resultados">
          <h2 className="text-base font-black text-slate-950">Evolução longitudinal dos resultados</h2>
          <p className="mb-4 mt-1 text-xs leading-relaxed text-slate-500">
            Séries organizadas por instrumento e condição de aplicação. As diferenças observadas acompanham a análise profissional registrada neste documento.
          </p>
          <AssessmentLongitudinalPanel sources={payload.sources} showCharts={payload.exportOptions.longitudinalCharts !== false} />
        </section>
      ) : null}

      <section
        className="rounded-[24px] border border-slate-200 bg-white shadow-sm print:shadow-none"
        aria-labelledby="assessment-report-results"
      >
        <div className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
          <span className="rounded-xl bg-slate-100 p-2 text-slate-600">
            <ListChecks className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <h2 id="assessment-report-results" className="text-base font-black text-slate-950">
              Resultados
            </h2>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
              Resultados e interpretações registrados para este documento.
            </p>
          </div>
        </div>

        {payload.sources.length > 0 ? (
          <div className="overflow-x-auto print:overflow-visible">
            <table className="w-full min-w-[760px] border-collapse text-left text-xs print:min-w-0">
              <caption className="sr-only">
                Avaliações incluídas, com data, domínio, resultado, classificação e interpretação
              </caption>
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">
                  <th scope="col" className="px-4 py-3">Avaliação</th>
                  <th scope="col" className="px-3 py-3">Data</th>
                  <th scope="col" className="px-3 py-3">Domínio</th>
                  <th scope="col" className="px-3 py-3">Resultado</th>
                  {hasClassification ? <th scope="col" className="px-3 py-3">Classificação</th> : null}
                  {hasInterpretation ? <th scope="col" className="px-4 py-3">Interpretação</th> : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payload.sources.map((source) => {
                  const interpretation = getSourceInterpretation(source);
                  const professional = source.professional.name?.trim();
                  const hasFindingClassification = source.finding.status === 'within_expected'
                    || source.finding.status === 'borderline'
                    || source.finding.status === 'altered';

                  return (
                    <tr key={source.resultId} className="align-top print:break-inside-avoid">
                      <th scope="row" className="px-4 py-3 font-bold leading-snug text-slate-900">
                        {source.snapshot.testLabel}
                        {professional ? (
                          <span className="mt-1 block text-[10px] font-medium leading-relaxed text-slate-500">
                            Responsável: {professional}
                          </span>
                        ) : null}
                      </th>
                      <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                        {formatCalendarDate(source.snapshot.assessmentDate) ?? 'Data não registrada'}
                      </td>
                      <td className="max-w-[150px] px-3 py-3 leading-relaxed text-slate-600">
                        {getAssessmentReportDomainLabel(source.finding.domain)}
                      </td>
                      <td className="max-w-[150px] px-3 py-3">
                        <span className="block font-black text-slate-900">{formatPrimaryMeasure(source)}</span>
                        <span className="mt-1 block text-[10px] leading-relaxed text-slate-500">
                          {source.snapshot.primaryMeasure.label}
                        </span>
                      </td>
                      {hasClassification ? (
                        <td className="max-w-[175px] px-3 py-3">
                          {hasFindingClassification ? <FindingStatusBadge status={source.finding.status} /> : null}
                        </td>
                      ) : null}
                      {hasInterpretation ? (
                        <td className="max-w-[240px] px-4 py-3 leading-relaxed text-slate-700">
                          {interpretation}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-5 py-6 text-sm text-slate-500">Nenhuma avaliação foi vinculada.</p>
        )}
      </section>

      {payload.mode === 'assessment_summary' ? (
        <>
          <NarrativeBlock
            title="Síntese profissional"
            text={payload.content.professionalOverview}
          />
          <NarrativeBlock title="Plano e reavaliação" text={payload.content.followUpGuidance} />
          <NarrativeBlock title="Observações do profissional" text={payload.content.observations.join('\n')} />
        </>
      ) : (
        <section aria-labelledby="assessment-report-technical-content">
          <div className="mb-3 flex items-center gap-2">
            <Stethoscope className="h-4 w-4 text-primary-700" aria-hidden="true" />
            <h2 id="assessment-report-technical-content" className="text-base font-black text-slate-950">
              Análise técnico-fisioterapêutica
            </h2>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <NarrativeBlock title="Contexto clínico-funcional" text={payload.content.clinicalContext} />
            <NarrativeBlock title="Síntese profissional" text={payload.content.integratedAnalysis} />
            <NarrativeBlock title="Conclusão fisioterapêutica" text={payload.content.physiotherapyConclusion} />
            <NarrativeBlock title="Observações do profissional" text={payload.content.limitations} />
          </div>
          {payload.content.recommendations.length > 0 ? (
            <section className="mt-4 rounded-[22px] border border-slate-200 bg-white p-5 shadow-sm print:break-inside-avoid print:shadow-none">
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">
                Plano e reavaliação
              </h3>
              <ol className="mt-3 space-y-2">
                {payload.content.recommendations.map((recommendation, index) => (
                  <li key={`${recommendation}-${index}`} className="flex items-start gap-3 text-sm leading-relaxed text-slate-700">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[10px] font-black text-primary-700">
                      {index + 1}
                    </span>
                    <span>{recommendation}</span>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </section>
      )}

      {hasCbdfContent ? (
        <section
          className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm print:shadow-none"
          aria-labelledby="assessment-report-cbdf"
        >
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-primary-50 p-2 text-primary-700">
              <Stethoscope className="h-4 w-4" aria-hidden="true" />
            </span>
            <div>
              <h2 id="assessment-report-cbdf" className="text-base font-black text-slate-950">
                Diagnóstico fisioterapêutico — CBDF
              </h2>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                Registro manual e autoral do fisioterapeuta; não calculado a partir dos escores.
                O RevaHub valida a estrutura, mas a transcrição e a existência do código
                permanecem sob conferência profissional na fonte oficial.
              </p>
            </div>
          </div>

          {payload.cbdf.entries.length > 0 ? (
            <div className="mt-4 space-y-3">
              {payload.cbdf.entries.map((entry) => (
                <div key={entry.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 print:break-inside-avoid">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-slate-900 px-2 py-1 font-mono text-xs font-bold text-white">
                      {entry.manualCode || entry.chapterCode}
                    </span>
                    <span className="text-xs font-semibold text-slate-600">
                      {getCbdfChapterLabel(entry.chapterCode)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-bold leading-relaxed text-slate-900">
                    {entry.professionalStatement}
                  </p>
                  {entry.rationale ? (
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-600">
                      Fundamentação registrada: {entry.rationale}
                    </p>
                  ) : null}
                  <div className="mt-3 space-y-1 text-xs leading-relaxed text-slate-600">
                    <p>
                      Confirmado por {entry.recordedBy.name || 'fisioterapeuta'}
                      {entry.recordedBy.registration ? ` · ${entry.recordedBy.registration}` : ''}
                      {formatCalendarDate(entry.recordedAt) ? ` · ${formatCalendarDate(entry.recordedAt)}` : ''}
                    </p>
                    {entry.evidenceResultIds.length > 0 ? (
                      <p>
                        Evidências: {entry.evidenceResultIds.map((id) => {
                          const source = payload.sources.find((item) => item.resultId === id);
                          return source
                            ? `${source.snapshot.testLabel} (${formatCalendarDate(source.snapshot.assessmentDate) ?? 'data não informada'})`
                            : id;
                        }).join('; ')}
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {payload.cbdf.professionalNote ? (
            <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
              {payload.cbdf.professionalNote}
            </p>
          ) : null}
        </section>
      ) : null}

      {hasIcfContent ? (
        <section
          className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm print:shadow-none"
          aria-labelledby="assessment-report-icf"
        >
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-primary-50 p-2 text-primary-700">
              <FileText className="h-4 w-4" aria-hidden="true" />
            </span>
            <div>
              <h2 id="assessment-report-icf" className="text-base font-black text-slate-950">
                Registros CIF selecionados
              </h2>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                Perfil parcial conforme o escopo deste relatório. Códigos e qualificadores
                selecionados manualmente pelo profissional na CIF {payload.icf.classificationVersion}
                {' '}({payload.icf.language}); a existência da categoria deve ser confirmada
                na edição oficial.
              </p>
            </div>
          </div>

          {payload.icf.professionalSynthesis ? (
            <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
              {payload.icf.professionalSynthesis}
            </p>
          ) : null}

          {icfEntryCount > 0 ? (
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {icfGroups.map((group) => (
                <IcfDomainGroup
                  key={group.domain}
                  domain={group.domain}
                  entries={group.entries}
                />
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      <footer className="flex flex-col gap-2 border-t border-slate-200 px-1 pt-4 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4" aria-hidden="true" />
          Conteúdo autoral registrado em {formatDateTime(payload.authorship.authoredAt) ?? 'data não registrada'}
        </div>
      </footer>
    </article>
  );
}
